import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Car, Clock, ArrowRight, BookOpen } from "lucide-react";
import { Footer } from "../components/Footer";
import axios from "axios";

const envBackend = process.env.REACT_APP_BACKEND_URL;
const API = envBackend ? `${envBackend}/api` : (window.APP_CONFIG?.API_URL || '/api');

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`)
      .then(r => setPosts(r.data.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[rgba(138,138,141,0.1)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img src="/logo-telobajo-new.png" alt="Te Lo Bajo" className="h-14 sm:h-16 w-auto" />
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/importacion" className="flex items-center gap-1.5 bg-[#FF3B30] hover:bg-[#E0352B] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-importacion">
              <span className="hidden sm:inline">Importación</span><span className="sm:hidden">Import</span>
            </Link>
            <Link to="/ocasion" className="flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#006AE0] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-nacionales">
              <span className="hidden sm:inline">Nacionales</span><span className="sm:hidden">Nacional</span>
            </Link>
            <Link to="/coches-disponibles" className="flex items-center gap-1.5 bg-[#34C759] hover:bg-[#2DB84E] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-stock">
              <Car className="w-4 h-4" /><span className="hidden sm:inline">Stock</span><span className="sm:hidden">Stock</span>
            </Link>
            <Link to="/blog" className="flex items-center gap-1.5 bg-[#F5A623] hover:bg-[#D4901E] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-blog">
              <BookOpen className="w-4 h-4" /><span className="hidden sm:inline">Blog</span><span className="sm:hidden">Blog</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-28 pb-12 px-4 sm:px-6">
        <div className="absolute inset-0 bg-cover bg-bottom bg-no-repeat" style={{ backgroundImage: "url('/hero-bg.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/90 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3" data-testid="blog-title">
            BLOG <span className="text-[#F5A623]">TE LO BAJO</span>
          </h1>
          <p className="text-[#8A8A8D] text-base sm:text-lg">Consejos, noticias y guías sobre importación de vehículos</p>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-[#8A8A8D]">Cargando entradas...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-[#636366] mx-auto mb-4" />
              <p className="text-[#8A8A8D] text-lg">Próximamente publicaremos contenido</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {posts.map(post => (
                <Link to={`/blog/${post.slug}`} key={post.id} className="group" data-testid={`blog-card-${post.slug}`}>
                  <article className="glass-card overflow-hidden border-[rgba(245,166,35,0.1)] hover:border-[#F5A623]/40 transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                    {post.cover_image && (
                      <div className="aspect-video overflow-hidden">
                        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <div className="p-5 flex flex-col flex-1">
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex gap-2 mb-3 flex-wrap">
                          {post.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="text-[10px] font-semibold uppercase tracking-wider text-[#F5A623] bg-[#F5A623]/10 px-2 py-0.5 rounded-full">{tag}</span>
                          ))}
                        </div>
                      )}
                      <h2 className="text-lg font-bold text-white mb-2 group-hover:text-[#F5A623] transition-colors">{post.title}</h2>
                      {post.excerpt && <p className="text-[#8A8A8D] text-sm leading-relaxed mb-4 flex-1">{post.excerpt}</p>}
                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-[rgba(138,138,141,0.1)]">
                        <span className="text-[#636366] text-xs flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(post.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="text-[#F5A623] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          Leer <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
